import React, { useCallback, useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid2,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseButton from "../../../Components/CloseButton";
import { PrimaryFormLoadingActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import VisualizationSettings from "./VisualizationSettings";
import VisualizationPreview from "./VisualizationPreview";
import { resetContinuousChartData } from "../Common/CommonFunctions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Cookies from "universal-cookie";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../Helper/Charts/ChartHelper";
import { getBatteryAlertIncidents } from "../../../Api/BmsFaultsApi";
import { convertZonetoUTCEnd, convertZonetoUTCStart, getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";

dayjs.extend(utc);
dayjs.extend(timezone);

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "1304px",
    maxWidth: "100%",
    backgroundColor: '#F4F4F4',
    margin: 0,
    maxHeight: '100%',
  },
}));

const CreateVisualizationDialog = ({ chartId, initialChartData = {}, batteryID, selectedDate, onSaveChart, batteryInfo = {}, selectedEntity = null }) => {

  const {t} = useTranslation(['common', 'digital_twin']);
  const [open, setOpen] = useState(false);
  const [previewChartData, setPreviewChartData] = useState(initialChartData);
  const isDisabled = !Boolean(batteryID);
  const [chartEntities, setChartEntities] = useState([]);
  const [chartEvents, setChartEvents] = useState([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomedRange, setZoomedRange] = useState(null);
  const zoomTimeoutRef = useRef(null);
  const [annotationData, setAnnotationData] = useState([]);

  const cookies = new Cookies();
  const timeZone = cookies.get("timeZone");

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchChartData = useCallback(async (entities, startDate, endDate, interval) => {
    if (!entities || entities.length === 0) {
      setPreviewChartData({});
      setIsChartLoading(false);
      return;
    }

    setIsChartLoading(true);

    try {
      const resetChartDataResponse = await resetContinuousChartData(entities, startDate, endDate, interval);
      const {metrics, series} = resetChartDataResponse;

      setPreviewChartData({
        metrics,
        series,
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setPreviewChartData({});
    } finally {
      setIsChartLoading(false);
    }
  }, []);

  const fetchEventsData = useCallback(async (events, startDate, endDate) => {
    if (!events || events.length === 0) {
      setIsChartLoading(false);
      return;
    }

    setIsChartLoading(true);

    try {
      // Format dates for API
      const startDateUTC = convertZonetoUTCStart(startDate);
      const endDateUTC = convertZonetoUTCEnd(startDate);

      const alertsFilters = {
        orgErrorCode: events.map(event => event.metric),
        triggerOrResolved: JSON.stringify({
          startDateUTC: startDateUTC,
          endDateUTC: endDateUTC
        })
      }

      const alertsResponse = await getBatteryAlertIncidents(
        {},
        batteryID,
        alertsFilters
      );
      const batteryAlerts = alertsResponse?.response?.data || [];

      // Convert timestamps to user timezone
      const convertedAlerts = batteryAlerts.map(alert => ({
        ...alert,
        customerDBTimeInsert: alert.customerDBTimeInsert
          ? getMetricTimeStamp(alert.customerDBTimeInsert, "YYYY-MM-DD HH:mm:ss")
          : alert.customerDBTimeInsert,
        resolveCustomerDBTime: alert.resolveCustomerDBTime
          ? getMetricTimeStamp(alert.resolveCustomerDBTime, "YYYY-MM-DD HH:mm:ss")
          : alert.resolveCustomerDBTime
      }));

      setAnnotationData(convertedAlerts);

    } catch (error) {
      console.error("Error fetching chart data:", error);
      setAnnotationData([]);
    } finally {
      setIsChartLoading(false);
    }
  }, [batteryID, timeZone]);

  const handleOnEntitiesChange = useCallback((entities) => {
    setChartEntities(entities);
    // Reset zoom state when entities change
    setIsZoomed(false);
    setZoomedRange(null);
    setIntervalSeconds(300);
    fetchChartData(entities, selectedDate?.value, selectedDate?.value, 300);
  }, [fetchChartData, selectedDate]);

  const handleOnEventsChange = useCallback((events) => {
    setChartEvents(events);
    fetchEventsData(events, selectedDate?.value, selectedDate?.value);
  }, [fetchEventsData, selectedDate]);

  const resetChartDataAfterZoom = useCallback((xaxis) => {
    // Clear any existing timeout
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }

    // Debounce the zoom update to prevent multiple rapid calls
    zoomTimeoutRef.current = setTimeout(async () => {

      // Use the xaxis parameter from beforeZoom event
      const minX = xaxis?.min;
      const maxX = xaxis?.max;

      if (!minX || !maxX) {
        setIsChartLoading(false);
        return;
      }

      // Calculate the appropriate interval based on the visible time window
      const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

      // Convert minX and maxX timestamps to date strings for the API
      const zoomedStartDate = formatChartTimestampForApi(minX);
      const zoomedEndDate = formatChartTimestampForApi(maxX);

      // Store zoom state
      setIsZoomed(true);
      setZoomedRange({ minX, maxX });
      setIntervalSeconds(calculatedInterval);

      // Fetch new data with the zoomed range
      await fetchChartData(chartEntities, zoomedStartDate, zoomedEndDate, calculatedInterval);
    }, 500); // 500ms debounce
  }, [chartEntities, fetchChartData]);

  const handleZoomReset = useCallback(async () => {
    // Reset interval to default (300 seconds / 5 minutes)
    const defaultInterval = 300;
    setIntervalSeconds(defaultInterval);

    // Reset zoom state
    setIsZoomed(false);
    setZoomedRange(null);

    // Refetch chart data with original date range and default interval
    await fetchChartData(chartEntities, selectedDate?.value, selectedDate?.value, defaultInterval);
  }, [chartEntities, fetchChartData, selectedDate]);

  const handleOnSave = () => {
    onSaveChart(chartEntities, chartEvents);
    handleClose();
  }

  return (
    <>
      <PrimaryFormLoadingActionButton
            onClick={handleOpen}
            sx={{
                backgroundColor: isDisabled ? '#E6E6E6' : '#6D72F6',
                color: isDisabled ? '#8F8F8F' : '#FFFFFF'
            }}
            disabled={isDisabled}
        >
            Create
        </PrimaryFormLoadingActionButton>

      <StyledDialog open={open} onClose={handleClose}>
        <DialogContent sx={{py: 3}}>
          <Grid2 sx={{ mb: 3 }}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
              <Typography variant="fw500s20c000000" sx={{ lineHeight: '24px' }}>Create Visualisation</Typography>
              <CloseButton onClick={handleClose} />
            </Box>
            <Grid2 container gap={1.5} flexWrap={'nowrap'}>
                <VisualizationSettings
                  entities={chartEntities}
                  batteryID={batteryID}
                  onEntitiesChange={handleOnEntitiesChange}
                  onEventsChange={handleOnEventsChange}
                />
                <VisualizationPreview
                  chartId={chartId}
                  selectedDate={selectedDate}
                  chartData={previewChartData}
                  onSave={handleOnSave}
                  isLoading={isChartLoading}
                  onZoom={resetChartDataAfterZoom}
                  onZoomReset={handleZoomReset}
                  zoomedRange={zoomedRange}
                  setLoading={setIsChartLoading}
                  isZoomed={isZoomed}
                  annotationData={annotationData}
                  batteryInfo={batteryInfo}
                  selectedEntity={selectedEntity}
                />
            </Grid2>
          </Grid2>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default CreateVisualizationDialog;
